import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { categoryContract } from "@common/contracts/category.contract";
import { Category } from "@common/types/category";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { invalidateTransactionsQueries } from "@components/transactions/useTransactionsQuery";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { getRuleGroupsQueryKey } from "./rules.api";

const categoryClientContract = getBackendAPIClient(categoryContract);

const CATEGORIES_STALE_TIME = 1000 * 60 * 5; // 5 minutes

const categoriesQueryKeys = {
    all: () => ["categories"] as const,
    query: (workspaceId: string) =>
        [...categoriesQueryKeys.all(), workspaceId] as const,
    global: () => [...categoriesQueryKeys.all(), "global"] as const,
};

export function useWorkspaceCategories() {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: categoriesQueryKeys.query(activeWorkspaceKey),
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await categoryClientContract.getCategories({
                params: {
                    workspaceId,
                },
            });

            return response.body;
        },
        staleTime: CATEGORIES_STALE_TIME,
    });
}

export function useGlobalCategories() {
    return useQuery({
        queryKey: categoriesQueryKeys.global(),
        queryFn: async () => {
            const response = await categoryClientContract.getGlobalCategories(
                {},
            );

            return response.body;
        },
        staleTime: CATEGORIES_STALE_TIME,
    });
}

export function useUpdateCustomCategoryMutation(category: Category) {
    const queryClient = useQueryClient();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof categoryContract.updateCategory
            >["body"],
        ) => {
            if (!category.workspaceId) {
                throw new Error("Can't update standard category");
            }

            const response = await categoryClientContract.updateCategory({
                params: {
                    workspaceId: category.workspaceId,
                    categoryId: category.id,
                },
                body,
            });

            return response.body;
        },
        onMutate: async (body) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            updateCategoryInQueryData(queryClient, { ...category, ...body });

            return category;
        },
        onError: (_, __, originalCategory) => {
            if (originalCategory) {
                updateCategoryInQueryData(queryClient, originalCategory);
            }
        },
    });
}

export function useCreateCustomCategoryMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof categoryContract.createCategory
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response = await categoryClientContract.createCategory({
                params: {
                    workspaceId,
                },
                body,
            });

            return response.body;
        },
        onSuccess: (createdCategory) => {
            addCategoryToQueryData(queryClient, createdCategory);
        },
    });
}

export function useCreateCategoryWithGlMappingsMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof categoryContract.createCategoryWithGlMappings
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response =
                await categoryClientContract.createCategoryWithGlMappings({
                    params: {
                        workspaceId,
                    },
                    body,
                });

            return response.body;
        },
        onSuccess: (createdCategory) => {
            addCategoryToQueryData(queryClient, createdCategory);
            void queryClient.invalidateQueries({
                queryKey: ["categoryGlMappings", workspaceId],
            });
        },
    });
}

interface UpdateCategoryGlMappingsVariables {
    categoryId: string;
    overrides: ServerInferRequest<
        typeof categoryContract.updateCategoryGlMappings
    >["body"]["overrides"];
    applyToExistingTransactions: boolean;
}

export function useUpdateCategoryGlMappingsMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async ({
            categoryId,
            overrides,
            applyToExistingTransactions,
        }: UpdateCategoryGlMappingsVariables) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response =
                await categoryClientContract.updateCategoryGlMappings({
                    params: {
                        workspaceId,
                        categoryId,
                    },
                    body: {
                        overrides,
                        applyToExistingTransactions,
                    },
                });

            return response.body;
        },
        onSuccess: (_data, variables) => {
            void Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ["categoryGlMappings", workspaceId],
                }),
                queryClient.invalidateQueries({
                    queryKey: categoriesQueryKeys.query(workspaceId ?? ""),
                }),
                queryClient.invalidateQueries({
                    queryKey: getRuleGroupsQueryKey(workspaceId ?? ""),
                }),
            ]);
            if (variables.applyToExistingTransactions) {
                invalidateTransactionsQueries();
            }
        },
    });
}

export function useDeleteCustomCategoryMutation(category: Category) {
    const queryClient = useQueryClient();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async () => {
            if (!category.workspaceId) {
                throw new Error("Can't delete standard category");
            }

            await categoryClientContract.deleteCategory({
                params: {
                    workspaceId: category.workspaceId,
                    categoryId: category.id,
                },
            });
        },
        onMutate: async () => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            removeCategoryFromQueryData(queryClient, category);
            invalidateTransactionsQueries();
        },
        onError: () => {
            // Nothing was removed, so re-adding would duplicate the row.
            if (isViewOnly) {
                return;
            }

            addCategoryToQueryData(queryClient, category);
        },
    });
}

function addCategoryToQueryData(
    queryClient: QueryClient,
    createdCategory: Category,
) {
    queryClient.setQueryData(
        categoriesQueryKeys.query(createdCategory.workspaceId ?? ""),
        (oldData: { categories: Category[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((category) =>
                    category.id === createdCategory.parentCategoryId
                        ? {
                              ...category,
                              subcategories: [
                                  ...(category.subcategories ?? []),
                                  createdCategory,
                              ],
                          }
                        : category,
                ),
            };
        },
    );
}

function removeCategoryFromQueryData(
    queryClient: QueryClient,
    removedCategory: Category,
) {
    queryClient.setQueryData(
        categoriesQueryKeys.query(removedCategory.workspaceId ?? ""),
        (oldData: { categories: Category[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((category) =>
                    category.id === removedCategory.parentCategoryId
                        ? {
                              ...category,
                              subcategories:
                                  category.subcategories?.filter(
                                      (subcategory) =>
                                          subcategory.id !== removedCategory.id,
                                  ) ?? [],
                          }
                        : category,
                ),
            };
        },
    );
}

function updateCategoryInQueryData(
    queryClient: QueryClient,
    updatedCategory: Category,
) {
    queryClient.setQueryData(
        categoriesQueryKeys.query(updatedCategory.workspaceId ?? ""),
        (oldData: { categories: Category[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((c) => ({
                    ...c,
                    subcategories:
                        c.subcategories?.map((subcategory) =>
                            subcategory.id === updatedCategory.id
                                ? updatedCategory
                                : subcategory,
                        ) ?? [],
                })),
            };
        },
    );
}
